import { useState, useEffect, useMemo, useCallback } from "react";
import { notification } from "antd";
import { getMyRequestsToHelp } from "../../API/getters";
import AindaSem from "../../Components/Graduado/AindaSem";
import PedidoColaboracao from "../../Components/Graduado/PedidoColaboracao";
import api from "../../API/api";
import useFormDraft from "../../hooks/useFormDraft";
import { Mail, Send, X, HelpCircle, Briefcase, BookOpen, DollarSign, Code, MoreHorizontal } from "lucide-react";

export default function PedidoColaboracaoPage() {
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [supportForm, setSupportForm] = useState({
    subject: "",
    category: "",
    description: "",
    attachment: null,
  });
  const [myRequestsToHelp, setMyRequestsToHelp] = useState([]);
  const [loading, setLoading] = useState(true);

  const draftFormData = useMemo(
    () => ({
      subject: supportForm.subject,
      category: supportForm.category,
      description: supportForm.description,
    }),
    [supportForm.subject, supportForm.category, supportForm.description],
  );

  const setDraftFormData = useCallback((data) => {
    setSupportForm((prev) => ({
      ...prev,
      subject: data.subject ?? "",
      category: data.category ?? "",
      description: data.description ?? "",
    }));
  }, []);

  const {
    markEdited,
    clearDraft,
    promptLeave,
    draftModals,
    draftBanner,
  } = useFormDraft({
    namespace: "pedido-colaboracao",
    data: draftFormData,
    setData: setDraftFormData,
    enabled: isSupportModalOpen,
    ready: isSupportModalOpen,
    notice: "O anexo terá de ser seleccionado novamente se fechar o formulário.",
  });

  const resetSupportForm = () => {
    setSupportForm({
      subject: "",
      category: "",
      description: "",
      attachment: null,
    });
  };

  useEffect(() => {
    const fetchMyRequestsToHelp = async () => {
      try {
        setLoading(true);
        const data = await getMyRequestsToHelp();
        setMyRequestsToHelp(data);
      } catch (error) {
        console.error("Erro ao carregar pedidos:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMyRequestsToHelp();
  }, []);

  const showSupportModal = () => {
    setIsSupportModalOpen(true);
  };

  const handleSupportModalOk = async () => {
    const form = new FormData();
    form.append("issue", supportForm.subject);
    form.append("category", supportForm.category);
    form.append("description", supportForm.description);
    if (supportForm.attachment) {
      form.append("attachment", supportForm.attachment);
    }

    try {
      const result = await api.post("v1/forms/help/", form);
      if (result.status === 201) {
        notification.success({
          message: "Sucesso",
          description: "Sua solicitação de colaboração foi enviada com sucesso.",
          placement: "topRight",
        });
        await clearDraft();
        // Recarregar a lista de pedidos
        const data = await getMyRequestsToHelp();
        setMyRequestsToHelp(data);
      } else {
        notification.error({
          message: "Erro",
          description: "Falha ao enviar sua solicitação. Tente novamente.",
          placement: "topRight",
        });
      }
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao enviar sua solicitação.",
        placement: "topRight",
      });
      console.error("Failed to submit support request:", error);
    }
    setIsSupportModalOpen(false);
    resetSupportForm();
  };

  const handleSupportModalCancel = () => {
    promptLeave(() => {
      setIsSupportModalOpen(false);
      resetSupportForm();
    });
  };

  const handleSupportFormChange = (e) => {
    const { name, value } = e.target;
    markEdited();
    setSupportForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "career":
        return <Briefcase className="w-4 h-4" />;
      case "academic":
        return <BookOpen className="w-4 h-4" />;
      case "financial":
        return <DollarSign className="w-4 h-4" />;
      case "technical":
        return <Code className="w-4 h-4" />;
      default:
        return <HelpCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="w-full py-6">
      {draftModals}

      {/* Lista de Pedidos */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
          <span className="text-gray-400 text-sm">Carregando pedidos...</span>
        </div>
      ) : myRequestsToHelp && myRequestsToHelp.length > 0 ? (
        <div className="space-y-4">
          {myRequestsToHelp.map((request) => (
            <PedidoColaboracao
              key={request.uuid}
              issue={request.issue}
              createdAt={request.created_at}
              answered={request.answered}
              category={request.category}
            />
          ))}
        </div>
      ) : (
        <div className="bg-gray-50/50 rounded-xl p-12 text-center border border-gray-100">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
              <HelpCircle className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <h3 className="text-lg font-light text-gray-700 mb-2">
            Nenhum pedido de colaboração
          </h3>
          <p className="text-gray-400 text-sm mb-6">
            Você ainda não realizou nenhuma solicitação de colaboração
          </p>
          <button
            onClick={showSupportModal}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            <Mail className="w-4 h-4" />
            Solicitar Colaboração
          </button>
        </div>
      )}

      {/* Modal de Solicitação */}
      {isSupportModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm" onClick={handleSupportModalCancel}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-emerald-500" />
                </div>
                <h2 className="text-lg font-medium text-gray-800">
                  Solicitar Colaboração
                </h2>
              </div>
              <button
                onClick={handleSupportModalCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {draftBanner}
              <form className="space-y-5">
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                    <HelpCircle className="w-3.5 h-3.5" />
                    Assunto
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={supportForm.subject}
                    onChange={handleSupportFormChange}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    placeholder="Ex: Orientação profissional, Apoio acadêmico..."
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                    <Briefcase className="w-3.5 h-3.5" />
                    Categoria
                  </label>
                  <select
                    name="category"
                    value={supportForm.category}
                    onChange={handleSupportFormChange}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white"
                  >
                    <option value="">Selecione uma categoria</option>
                    <option value="career">Carreira</option>
                    <option value="academic">Acadêmico</option>
                    <option value="financial">Financeiro</option>
                    <option value="technical">Técnico</option>
                    <option value="other">Outro</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                    <Mail className="w-3.5 h-3.5" />
                    Descrição
                  </label>
                  <textarea
                    name="description"
                    value={supportForm.description}
                    onChange={handleSupportFormChange}
                    rows={5}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                    placeholder="Descreva detalhadamente sua solicitação de colaboração..."
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                    <Send className="w-3.5 h-3.5" />
                    Anexo (opcional)
                  </label>
                  <input
                    type="file"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-600 hover:file:bg-emerald-100"
                    onChange={(e) => {
                      markEdited();
                      setSupportForm((prev) => ({
                        ...prev,
                        attachment: e.target.files[0],
                      }));
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Formatos aceitos: PDF, DOC, DOCX, JPG, PNG (max. 5MB)
                  </p>
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={handleSupportModalCancel}
                className="px-5 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSupportModalOk}
                className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors flex items-center gap-2 shadow-sm"
              >
                <Send className="w-4 h-4" />
                Enviar Solicitação
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}